import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { catchError, EMPTY, finalize, forkJoin, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { AppLoaderService } from 'src/app/services/app-loader.service';
import { UIGeneration } from '../../reducers/generations/generation.reducer';
import { GenerationStatus } from 'src/app/components/generations-wizard/types';
import { UtilService } from 'src/app/services/util.service';
import { GenerationActions } from '../../actions/generations/generation.actions';
import { TranslateService } from '@ngx-translate/core';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { ApiConstants } from 'src/app/base/api-constants';
import { GlobalConstants } from 'src/app/base/global.constants';
import { Store } from '@ngrx/store';
import { setAppMetaInfo } from '../../actions/applifecycle.actions';

// Adapter function to transform generation response
const transformGenerationResponse = (gen: any): UIGeneration => {
  /**
   * states where the generation is considered complete and locked for editing.
   */
  const FINALIZED_STATUSES = [
    GenerationStatus.COMPLETED,
    GenerationStatus.MERGE_FAILED,
    GenerationStatus.MERGE_COMPLETED_AND_PROTOTYPE_FAILED
  ];
  return {
    ...gen,
    id: gen.sid,
    name: gen.name,
    autoPublish: gen.prototypeCreationNeeded,
    status: gen.status,
    currentStep: gen.currentStep,
    globalReadOnly: FINALIZED_STATUSES.includes(gen.status),
    steps: [...gen.availableSteps]
  };
};

@Injectable()
export class GenerationsEffects {
  private readonly actions = inject(Actions);
  private readonly httpService = inject(HttpBaseService);
  private readonly loaderService = inject(AppLoaderService);
  private readonly utilService = inject(UtilService);
  private readonly translateService = inject(TranslateService);
  private readonly appGlobalService = inject(AppGlobalService);
  private readonly store = inject(Store);

  getAllGenerations$ = createEffect(() =>
    this.actions.pipe(
      ofType(GenerationActions.getAllGenerations),
      tap(() => {
        this.loaderService.show();
      }),
      switchMap((action: any) => {
        const { apiConstants, filter } = action;
        return this.httpService[apiConstants.method](apiConstants, filter).pipe(
          map((response: any) => {
            const transformedGenerations: UIGeneration[] = response.results.map(transformGenerationResponse);
            return GenerationActions.setGenerations({ generations: transformedGenerations });
          }),
          finalize(() => {
            this.loaderService.hide();
          })
        );
      })
    )
  );

  createGeneration$ = createEffect(() =>
    this.actions.pipe(
      ofType(GenerationActions.createGeneration),
      tap(() => {
        this.loaderService.show();
      }),
      switchMap((action: any) => {
        const { apiConstants, data } = action;
        return this.httpService[apiConstants.method](apiConstants, data).pipe(
          map((response: any) => {
            const transformedGeneration = transformGenerationResponse(response);
            return GenerationActions.createGenerationSuccess({ generation: transformedGeneration });
          }),
          catchError((error) => of(GenerationActions.createGenerationFailure({ error }))),
          finalize(() => {
            this.loaderService.hide();
          })
        );
      })
    )
  );

  createGenerationSuccess$ = createEffect(() =>
    this.actions.pipe(
      ofType(GenerationActions.createGenerationSuccess),
      mergeMap((action) => {
        const { generation } = action;
        return of(
          GenerationActions.addGeneration({ generation }),
          GenerationActions.setCurrentGeneration({ generationId: generation.sid })
        );
      }),
      catchError(() => EMPTY)
    )
  );

  createGenerationFailure$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(GenerationActions.createGenerationFailure),
        tap((response) => {
          if (response.error?.error) {
            this.utilService.showError(this.translateService.instant(response.error.error.ERROR_CODE));
            return;
          }
          this.utilService.showError('Failed to create generation. Please try again.');
        })
      ),
    { dispatch: false }
  );

  updateGeneration$ = createEffect(() =>
    this.actions.pipe(
      ofType(GenerationActions.updateGenerationAPI),
      tap(() => {
        this.loaderService.show();
      }),
      switchMap((action: any) => {
        const { apiConstants, data } = action;
        return this.httpService[apiConstants.method](apiConstants, data).pipe(
          map((_) => {
            const { appId, orgId, ...rest } = data;
            return GenerationActions.updateGeneration({ generation: { ...rest, id: rest.sid } });
          }),
          catchError((error) => of(GenerationActions.updateGenerationFailure({ error }))),
          finalize(() => {
            this.loaderService.hide();
          })
        );
      })
    )
  );

  updateGenerationFailure$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(GenerationActions.updateGenerationFailure),
        tap(() => {
          this.utilService.showError('Failed to update generation. Please try again.');
        })
      ),
    { dispatch: false }
  );

  getGenerationById$ = createEffect(() =>
    this.actions.pipe(
      ofType(GenerationActions.getGenerationById),
      tap(() => this.loaderService.show()),
      switchMap((action: any) => {
        const { apiConstants, urlPathVariables } = action;
        return this.httpService[apiConstants.method](apiConstants, urlPathVariables).pipe(
          map((response: any) => {
            const transformedGeneration = transformGenerationResponse(response);
            return GenerationActions.updateGeneration({ generation: transformedGeneration });
          }),
          catchError((error) => EMPTY),
          finalize(() => {
            this.loaderService.hide();
          })
        );
      })
    )
  );

  completeGeneration$ = createEffect(() =>
    this.actions.pipe(
      ofType(GenerationActions.completeGeneration),
      switchMap((action: any) => {
        const { apiConstants, urlPathVariables } = action;
        return this.httpService[apiConstants.method](apiConstants, urlPathVariables).pipe(
          map((response: any) => {
            const isNull = response === null;
            if (isNull) {
              // Empty null interpreted as success scenario (no failed ingestions remaining)
              return GenerationActions.completeGenerationSuccess();
            } else {
              throw new Error('Unexpected response format');
            }
          }),
          catchError((error) => of(GenerationActions.completeGenerationFailure()))
        );
      })
    )
  );

  completeGenerationSuccess$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(GenerationActions.completeGenerationSuccess),
        tap(() => {
          this.utilService.showSuccess('Generation completed successfully.');
          this.refreshAppMetaInfo();
        })
      ),
    { dispatch: false }
  );

  compleGenerationFailure$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(GenerationActions.completeGenerationFailure),
        tap(() => {
          this.utilService.showError('Failed to complete generation. Please try again.');
          return EMPTY;
        })
      ),
    { dispatch: false }
  );

  private refreshAppMetaInfo(): void {
    const appId = this.appGlobalService.get('appId');
    const orgId = this.appGlobalService.get('orgId');
    if (!appId || appId === 'appId') return;

    const batchSize = GlobalConstants.apiCallBatchSize;

    const countSubs$ = {
      appMetaInfoCount: this.httpService.get(ApiConstants.getAppMetaInfoCount, { appId }),
      appUserMetaInfoCount: this.httpService.get(ApiConstants.getAppUserMetaInfoCount, { orgId })
    };

    forkJoin(countSubs$).subscribe({
      next: ({ appMetaInfoCount, appUserMetaInfoCount }) => {
        const appMetaInfoFetchSubs$: any[] = [];
        for (let i = 0; i < Math.ceil(parseInt(appMetaInfoCount as string) / batchSize); i++) {
          appMetaInfoFetchSubs$.push(
            this.httpService.post(ApiConstants.getAppMetaInfo, { start: i * batchSize, length: batchSize })
          );
        }

        const appUserMetaInfoFetchSubs$: any[] = [];
        for (let j = 0; j < Math.ceil(parseInt(appUserMetaInfoCount as string) / batchSize); j++) {
          appUserMetaInfoFetchSubs$.push(
            this.httpService.post(ApiConstants.getAppUserMetaInfo, { start: j * batchSize, length: batchSize })
          );
        }

        const allFetches$ = {
          appMetaInfoResponses: appMetaInfoFetchSubs$.length > 0 ? forkJoin(appMetaInfoFetchSubs$) : of([]),
          appUserMetaInfoResponses: appUserMetaInfoFetchSubs$.length > 0 ? forkJoin(appUserMetaInfoFetchSubs$) : of([])
        };

        forkJoin(allFetches$).subscribe({
          next: ({ appMetaInfoResponses, appUserMetaInfoResponses }) => {
            const metaInfo = JSON.parse(JSON.stringify(this.appGlobalService.get('appmetaInfo'))) || {};
            const queryStrings = JSON.parse(JSON.stringify(this.appGlobalService.get('queryStrings'))) || {};
            let refMetaInfo = JSON.parse(JSON.stringify(this.appGlobalService.get('refAppMetaInfos'))) || {};

            (appMetaInfoResponses as any[]).forEach((res: any) => {
              res.results.forEach((rec) => {
                metaInfo[rec.modelId] = rec.name;
                if (rec.modelType === 'QUERY_STRING') queryStrings[rec.modelId] = rec.name;
                if (rec.appId !== appId) {
                  refMetaInfo[rec.modelId] = rec.name;
                }
              });
            });

            (appUserMetaInfoResponses as any[]).forEach((res: any) => {
              res.results.forEach((rec) => {
                metaInfo[rec.modelId] = rec.name;
              });
            });

            this.appGlobalService.write('appmetaInfo', metaInfo);
            this.appGlobalService.write('queryStrings', queryStrings);
            this.appGlobalService.write('refAppMetaInfos', refMetaInfo);
            this.store.dispatch(setAppMetaInfo({ metaInfo }));
          },
          error: (err) => console.error('Error refreshing app meta info:', err)
        });
      },
      error: (err) => console.error('Error fetching meta info counts:', err)
    });
  }
}
